import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { PackageIcon } from "@/components/InlineIcons";
import { CALC } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

const Field = ({ label, testId, type = "text", value, onChange, prefix, suffix, step, placeholder }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-slate-500">
      {label}
    </Label>
    <div className="relative">
      {prefix && (
        <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-slate-400 text-sm">
          {prefix}
        </span>
      )}
      <Input
        data-testid={testId}
        type={type}
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)}
        className={`bg-white border-slate-300 rounded-lg transition-shadow focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 h-10 tabular placeholder:text-slate-300 dark:placeholder:text-slate-600 ${prefix ? "ps-7" : ""} ${suffix ? "pe-8" : ""}`}
      />
      {suffix && (
        <span className="absolute inset-y-0 end-0 flex items-center pe-3 text-slate-400 text-sm">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

export const LoadInfoForm = ({ lang, load, setLoad }) => {
  const t = useT(lang);
  const upd = (key) => (v) => setLoad((p) => ({ ...p, [key]: v }));

  return (
    <Card
      data-testid="load-info-form"
      className="p-5 md:p-6 rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.04)]"
    >
      <div className="flex items-center gap-2 mb-4">
        <PackageIcon />
        <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-700 dark:text-slate-200">
          {t("loadInfo")}
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label={t("pickCity")}
          testId={CALC.inputPickCity}
          value={load.pick_city}
          onChange={upd("pick_city")}
          placeholder="e.g. Danville, KY"
        />
        <Field
          label={t("deliveryCity")}
          testId={CALC.inputDeliveryCity}
          value={load.delivery_city}
          onChange={upd("delivery_city")}
          placeholder="e.g. Charlotte, NC"
        />
        <Field
          label={t("loadedMiles")}
          testId={CALC.inputLoadedMiles}
          type="number"
          value={load.loaded_miles}
          onChange={upd("loaded_miles")}
        />
        <Field
          label={t("emptyMiles")}
          testId={CALC.inputEmptyMiles}
          type="number"
          value={load.empty_miles}
          onChange={upd("empty_miles")}
        />
        <Field
          label={t("pickupTime")}
          testId={CALC.inputPickupTime}
          type="datetime-local"
          value={load.pickup_time}
          onChange={upd("pickup_time")}
        />
        <Field
          label={t("deliveryTime")}
          testId={CALC.inputDeliveryTime}
          type="datetime-local"
          value={load.delivery_time}
          onChange={upd("delivery_time")}
        />
        <Field
          label={t("weight")}
          testId={CALC.inputWeight}
          type="number"
          value={load.weight}
          onChange={upd("weight")}
          suffix="lbs"
        />
        <Field
          label={t("commodity")}
          testId={CALC.inputCommodity}
          value={load.commodity}
          onChange={upd("commodity")}
          placeholder="e.g. Dry Foods"
        />
        <div className="sm:col-span-2">
          <Field
            label={t("rate")}
            testId={CALC.inputRate}
            type="number"
            step="0.01"
            value={load.rate}
            onChange={upd("rate")}
            prefix="$"
          />
        </div>
      </div>
    </Card>
  );
};

export const CostSettingsForm = ({ lang, load, setLoad }) => {
  const t = useT(lang);
  const upd = (key) => (v) => setLoad((p) => ({ ...p, [key]: v }));

  return (
    <Card className="p-5 md:p-6 rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2 h-2 rounded-full bg-blue-600" />
        <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-700 dark:text-slate-200">
          {t("costSettings")}
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label={t("costPerMile")}
          testId={CALC.inputCostPerMile}
          type="number"
          step="0.01"
          value={load.cost_per_mile}
          onChange={upd("cost_per_mile")}
          prefix="$"
        />
        <Field
          label={t("driverPayPerMile")}
          testId={CALC.inputDriverPay}
          type="number"
          step="0.01"
          value={load.driver_pay_per_mile}
          onChange={upd("driver_pay_per_mile")}
          prefix="$"
        />
        <Field
          label={t("dispatcherFee")}
          testId={CALC.inputDispatcherFee}
          type="number"
          step="0.1"
          value={load.dispatcher_fee_pct}
          onChange={upd("dispatcher_fee_pct")}
          suffix="%"
        />
        <Field
          label={t("minProfit")}
          testId={CALC.inputMinProfit}
          type="number"
          step="1"
          value={load.min_profit_goal}
          onChange={upd("min_profit_goal")}
          prefix="$"
        />
      </div>
    </Card>
  );
};
